/**
 * Frontend-driven email renderer.
 *
 * Generates an HTML email from the Message[] displayed in the UI, including
 * thought process steps (tool calls, SQL queries, searches) that were captured
 * from progress frames during the live session.
 *
 * This renders exactly what the user sees — respecting hidden turns (already
 * filtered out of the messages array before it reaches this function).
 */

interface EmailMessage {
  text: string;
  isUser: boolean;
  thoughtProcess?: string[];
}

/**
 * Render the current chat messages to an HTML email string.
 * Includes thought process steps (tool calls, SQL, searches) for agent messages.
 */
export function renderConversationEmail(
  messages: EmailMessage[],
  conversationName: string,
  userAlias: string,
): string {
  const messageRows = messages
    .map((msg) => renderMessage(msg, userAlias))
    .join('\n<tr><td style="height: 8px;"></td></tr>\n');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; background: #f5f5f5; color: #1a1a1a; }
    .container { max-width: 800px; margin: 0 auto; background: #ffffff; border-radius: 8px; padding: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .header { border-bottom: 2px solid #4caf50; padding-bottom: 12px; margin-bottom: 20px; }
    .header h1 { margin: 0; font-size: 20px; color: #333; }
    .header p { margin: 4px 0 0; font-size: 13px; color: #666; }
    table.messages { width: 100%; border-collapse: separate; border-spacing: 0; }
    .user-msg { padding: 12px; background-color: #e3f2fd; border-radius: 8px; }
    .agent-msg { padding: 12px; background-color: #f1f8e9; border-radius: 8px; }
    .thought-process { margin: 8px 0; padding: 8px 12px; background: #fafafa; border-left: 3px solid #e0e0e0; border-radius: 4px; font-size: 12px; color: #555; }
    .thought-process .label { font-weight: 600; color: #333; margin-bottom: 6px; }
    .thought-step { margin: 4px 0; padding: 4px 0; border-bottom: 1px solid #f0f0f0; }
    .thought-step:last-child { border-bottom: none; }
    pre { background: #1e1e1e; color: #d4d4d4; padding: 12px; border-radius: 6px; overflow-x: auto; font-size: 13px; }
    code { background: #e8e8e8; padding: 2px 5px; border-radius: 3px; font-size: 0.9em; }
    pre code { background: none; padding: 0; }
    table.data-table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 13px; }
    table.data-table th, table.data-table td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; }
    table.data-table th { background: #f5f5f5; font-weight: 600; }
    blockquote { border-left: 3px solid #ccc; margin: 8px 0; padding-left: 12px; color: #666; }
    .footer { margin-top: 20px; padding-top: 12px; border-top: 1px solid #eee; font-size: 11px; color: #999; text-align: center; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🥫 ${escapeHtml(conversationName)}</h1>
      <p>Exported from Mr. Pibb on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
    </div>
    <table class="messages">
      ${messageRows}
    </table>
    <div class="footer">
      Sent from <a href="https://mrpibb.ai.amazon.dev">Mr. Pibb</a> — PV Sports AI Assistant
    </div>
  </div>
</body>
</html>`;
}

/** Render a single message (user or agent) */
function renderMessage(msg: EmailMessage, userAlias: string): string {
  const sender = msg.isUser ? escapeHtml(userAlias) : '🥫 Mr. Pibb';
  const cssClass = msg.isUser ? 'user-msg' : 'agent-msg';
  const content = msg.isUser ? escapeHtml(msg.text) : markdownToHtml(msg.text);

  // Thought process section (agent messages only)
  let thoughtHtml = '';
  if (!msg.isUser && msg.thoughtProcess && msg.thoughtProcess.length > 0) {
    const steps = msg.thoughtProcess
      .map(step => `<div class="thought-step">${markdownToHtml(step)}</div>`)
      .join('');
    thoughtHtml = `
      <div class="thought-process">
        <div class="label">🧠 Thought Process (${msg.thoughtProcess.length} steps)</div>
        ${steps}
      </div>`;
  }

  return `
    <tr><td class="${cssClass}">
      <strong>${sender}</strong>
      ${thoughtHtml}
      <div style="margin-top: 8px;">${content}</div>
    </td></tr>`;
}

/** Convert basic markdown to HTML for email rendering */
function markdownToHtml(md: string): string {
  // Extract code blocks before escaping
  const codePlaceholders: string[] = [];
  let processed = md.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, lang, code) => {
    const idx = codePlaceholders.length;
    const label = lang ? `<div style="font-size:11px;color:#999;margin-bottom:4px;">${escapeHtml(lang.toUpperCase())}</div>` : '';
    codePlaceholders.push(`${label}<pre><code>${escapeHtml(code)}</code></pre>`);
    return `__CODE_${idx}__`;
  });

  let html = escapeHtml(processed);

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Restore code blocks
  codePlaceholders.forEach((content, idx) => {
    html = html.replace(`__CODE_${idx}__`, content);
  });

  // Headers
  html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Markdown tables
  html = html.replace(/^\|(.+)\|\s*\n\|[-|\s]+\|\s*\n((?:\|.+\|\s*\n?)*)/gm, (_match, headerRow, bodyRows) => {
    const headers = headerRow.split('|').map((h: string) => h.trim()).filter(Boolean);
    const rows = bodyRows.trim().split('\n').map((row: string) =>
      row.split('|').map((c: string) => c.trim()).filter(Boolean)
    );
    const thCells = headers.map((h: string) => `<th>${h}</th>`).join('');
    const trRows = rows.map((row: string[]) => `<tr>${row.map((c: string) => `<td>${c}</td>`).join('')}</tr>`).join('');
    return `<table class="data-table"><thead><tr>${thCells}</tr></thead><tbody>${trRows}</tbody></table>`;
  });

  // Blockquotes
  html = html.replace(/^&gt;\s+(.+)$/gm, '<blockquote>$1</blockquote>');

  // Unordered lists
  html = html.replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>');
  html = html.replace(/((?:<li>.*<\/li>\s*)+)/g, '<ul>$1</ul>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');
  html = `<p>${html}</p>`;
  html = html.replace(/<p>\s*<\/p>/g, '');

  return html;
}

/** Escape HTML special characters */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
